import styles from './UniversalDropdown.module.scss';
import Select from 'react-select';
import {
  assetTypeIcons,
  customColors,
  sortAlphabeticalOrder,
  vehicleString,
} from '../../utilities';
import { useCallback, useMemo, useState } from 'react';
import DropdownCard from './DropdownCard';
import {
  getTranslationKey,
  keys,
} from '../../utilities/translator/translation_keys';
import { useTranslation } from 'react-i18next';
import { useCurrentUser } from '../../providers/UserProvider';
import Icon from '../Icon';
import { Text } from '../typography';
import { allTypesOption } from '../../constants/strings';

const sharedWorkspaceOption = {
  label: 'Company Shared',
  icon: 'public',
  color: customColors.BLUE,
  value: `${null}`,
  title: 'Company Shared',
  id: 'company_shared',
};

const AllWorkspacesPlaceholder = () => {
  return (
    <div className={styles.flexRow}>
      <Icon style={{ color: customColors.GREEN, fontSize: '1.2rem' }}>
        check_circle
      </Icon>
      <Text noMargin>All Workspaces</Text>
    </div>
  );
};

export default function UniversalDropdown({
  selected = null,
  onChange = () => {},
  options = [],
  isMulti = false,
  isClearable = false,
  closeMenuOnSelect,
  type = null,
  label = null,
  isRequired = false,
  readOnly = false,
  placeholder = null,
  hideArrow = false,
  showPlaceholder = true,
  size = 'md',
  onClear,
}) {
  const { allTemplateTypes } = useCurrentUser();
  const { t } = useTranslation();
  const [focused, setFocused] = useState(false);

  const getCardData = useCallback(
    (data, isMenu) => {
      const display = {
        id: data?.id,
        value: data?.value,
        title: '',
        icon: null,
        img: null,
        color: undefined,
        menuIcon: 'star_outlined',
      };

      switch (type) {
        case 'user':
        case 'creator':
          return {
            ...display,
            title: `${data?.firstName ?? ''} ${data?.lastName ?? ''}`.trim(),
            img: data?.avatarUrl,
          };
        case 'workspace':
        case 'workspace_not_null':
        case 'workspaces':
          return {
            ...display,
            title: data?.title,
            img: data?.logoUrl,
            icon: 'public',
            iconProps: {
              color: customColors.BLUE,
              fontSize: isMulti && !isMenu ? '1rem' : '1.2rem',
            },
          };
        case 'asset':
          return {
            ...display,
            title: vehicleString(data),
            img: data?.imageUrl,
            icon: assetTypeIcons[data.type] || 'local_shipping',
            smallText: !isMenu
              ? ''
              : data?.workspaces?.length
              ? data?.workspaces?.map((ws) => ws.title).join(', ')
              : 'No Workspaces',
          };
        case 'template':
        case 'templateType': {
          const t =
            data?.templateType?.toLowerCase() ||
            data?.key?.toLowerCase() ||
            data?.name;
          const templateType = t === 'vehicle' ? 'fleet' : t;
          const info =
            templateType.key === 'ALL_TYPES'
              ? { allTypes: allTypesOption }
              : allTemplateTypes[templateType];
          const icon = info?.icon || 'article';
          // Archived shows regardless of isMenu (unlike the workspace/shared
          // subtext below) — a template can appear here as an already-
          // selected value (the collapsed chip, isMenu === false) even
          // though the picker itself only offers active ones, since an
          // existing filter can reference one that's since been archived.
          const smallText = data?.isArchived
            ? 'Archived'
            : !isMenu
            ? ''
            : type === 'templateType'
            ? ''
            : data?.workspace?.title ?? 'Company Shared';

          return {
            ...display,
            title: data?.name || data?.label || data?.title || data?.key,
            icon: icon || data?.icon || info?.icon || 'article',
            iconProps: {
              color: data?.isArchived
                ? customColors.SECONDARY
                : customColors[info?.color.toUpperCase()] ||
                  customColors[data?.color?.toUpperCase()] ||
                  data?.color ||
                  info?.color ||
                  customColors.SECONDARY,
            },
            shared: type === 'template' && !data?.workspace?.id,
            smallText,
          };
        }
        case 'risk':
          return {
            ...display,
            title: data?.category || `RiskID: ${data.id}`,
          };
        case 'permissionGrant':
          return data?.entityType === 'user'
            ? {
                ...display,
                title: `${data?.firstName ?? ''} ${
                  data?.lastName ?? ''
                }`.trim(),
                img: data?.avatarUrl,
              }
            : {
                ...display,
                title: data?.title,
                icon: 'group',
                iconProps: {
                  color: customColors.BLUE,
                  fontSize: isMulti && !isMenu ? '1rem' : '1.2rem',
                },
              };
        default:
          return {
            ...display,
            icon: data?.icon,
            title:
              label || data?.title || data?.label || data?.name || 'No Title',
          };
      }
    },
    [allTemplateTypes, isMulti, label, type],
  );

  const formattedOptions = useMemo(() => {
    const o =
      options?.map((o) =>
        typeof o === 'string' ? { label: o, value: o } : o,
      ) || [];
    return sortAlphabeticalOrder(o, 'key') || o;
  }, [options]);

  const nullValue = useMemo(() => {
    switch (type) {
      case 'workspace':
      case 'workspaces':
        return sharedWorkspaceOption;
      default:
        return null;
    }
  }, [type]);

  const groupedAssetOptions = useMemo(() => {
    if (type !== 'asset') return null;
    const groups = {};
    for (const option of formattedOptions) {
      const key = option.type || 'Other';
      if (!groups[key]) groups[key] = [];
      groups[key].push(option);
    }
    return Object.keys(groups)
      .sort()
      .map((groupKey) => ({
        label: groupKey,
        options: groups[groupKey].sort((a, b) =>
          vehicleString(a).localeCompare(vehicleString(b)),
        ),
      }));
  }, [formattedOptions, type]);

  const groupedTemplateOptions = useMemo(() => {
    if (type !== 'template') return null;
    const groups = {};
    for (const option of formattedOptions) {
      const rawType = option.templateType?.toLowerCase() || 'other';
      const key = rawType === 'vehicle' ? 'fleet' : rawType;
      if (!groups[key]) groups[key] = [];
      groups[key].push(option);
    }
    return Object.keys(groups)
      .sort()
      .map((groupKey) => ({
        label: groupKey,
        options: groups[groupKey].sort((a, b) =>
          (a.title || '').localeCompare(b.title || ''),
        ),
      }));
  }, [formattedOptions, type]);

  const groupedPermissionGrantOptions = useMemo(() => {
    if (type !== 'permissionGrant') return null;
    const groups = { Roles: [], Users: [] };
    for (const option of formattedOptions) {
      const key = option.entityType === 'user' ? 'Users' : 'Roles';
      groups[key].push(option);
    }
    return Object.keys(groups)
      .filter((groupKey) => groups[groupKey].length > 0)
      .map((groupKey) => ({
        label: groupKey,
        options: groups[groupKey].sort((a, b) =>
          (a.title || `${a.firstName ?? ''} ${a.lastName ?? ''}`).localeCompare(
            b.title || `${b.firstName ?? ''} ${b.lastName ?? ''}`,
          ),
        ),
      }));
  }, [formattedOptions, type]);

  const baseOptions = useMemo(() => {
    if (type === 'asset') return groupedAssetOptions || [];
    if (type === 'template') return groupedTemplateOptions || [];
    if (type === 'permissionGrant') return groupedPermissionGrantOptions || [];
    return type === 'workspace' || type === 'workspaces'
      ? [sharedWorkspaceOption, ...formattedOptions]
      : formattedOptions;
  }, [
    formattedOptions,
    groupedAssetOptions,
    groupedTemplateOptions,
    groupedPermissionGrantOptions,
    type,
  ]);

  const optionsToUse = useMemo(() => {
    if (type === 'asset' || type === 'template' || type === 'permissionGrant')
      return baseOptions;
    if (!nullValue) return baseOptions;
    const hasNull = baseOptions.some(
      (o) => (o?.id ?? o?.value) === (nullValue.id ?? nullValue.value),
    );
    return hasNull ? baseOptions : [nullValue, ...baseOptions];
  }, [baseOptions, nullValue, type]);

  return (
    <div className={styles.selectContainer}>
      <Select
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        isDisabled={readOnly}
        placeholder={
          (type === 'workspace_not_null' && showPlaceholder) ||
          type === 'workspaces' ? (
            <AllWorkspacesPlaceholder />
          ) : (
            `${t(keys.utilities.SEARCH_OR_SELECT)} ${
              placeholder ||
              t(getTranslationKey(type, 'common' || 'workspace')) ||
              '...'
            }`
          )
        }
        menuPortalTarget={
          typeof document !== 'undefined' ? document.body : undefined
        }
        menuPosition="fixed"
        menuShouldScrollIntoView={false}
        theme={(theme) => ({
          ...theme,
          colors: {
            ...theme.colors,
            primary: 'white',
            primary25: 'rgba(0,0,0,0.06)',
            primary50: 'rgba(0,0,0,0.12)',
          },
        })}
        styles={{
          control: (base, state) => {
            const border = isRequired
              ? 'rgba(227, 59, 59, 0.5)'
              : customColors.SECONDARY_LIGHT;
            return {
              ...base,
              borderRadius: '8px',
              height: '100%',
              width: '100%',
              maxWidth: '100rem',
              minWidth: '100%',
              backgroundColor: 'white',
              borderColor: border,
              boxShadow: 'none',
              '&:hover': {
                borderColor: border,
              },
            };
          },
          menuPortal: (base) => ({
            ...base,
            zIndex: 2147483647,
          }),
          valueContainer: (base) => ({
            ...base,
          }),
          dropdownIndicator: (base) => ({
            ...base,
            display: hideArrow ? 'none' : base?.display,
          }),
          indicatorsContainer: (base) => ({
            ...base,
            display: hideArrow ? 'none' : base?.display,
          }),
          indicatorSeparator: (base) => ({
            ...base,
            display: hideArrow ? 'none' : base?.display,
          }),
          group: (base) => ({
            ...base,
            paddingTop: 0,
            paddingBottom: 0,
          }),
          option: (base, { isSelected, isFocused }) => ({
            ...base,
            backgroundColor: isSelected
              ? 'white'
              : isFocused
              ? customColors.OFF_WHITE
              : base.backgroundColor,
            color: 'inherit',
            padding: '0.3rem 0.75rem',
          }),
        }}
        formatGroupLabel={
          type === 'asset' || type === 'template' || type === 'permissionGrant'
            ? (group) => {
                const displayName =
                  type === 'permissionGrant'
                    ? group.label
                    : type === 'template'
                    ? allTemplateTypes[group.label]?.name
                    : group.label;
                return (
                  <div className={styles.indicator}>
                    <Text
                      noMargin
                      size="sm"
                      weight="semiBold"
                      textAlign="center"
                      color="secondary"
                    >
                      {displayName}
                    </Text>
                  </div>
                );
              }
            : undefined
        }
        isMulti={isMulti}
        isClearable={isClearable}
        closeMenuOnSelect={closeMenuOnSelect}
        isSearchable
        options={optionsToUse}
        value={!!selected ? selected : nullValue}
        getOptionValue={(o) => String(o?.id ?? o?.value)}
        getOptionLabel={(o) => getCardData(o).title || ''}
        formatOptionLabel={(option, { context }) => {
          const isMenu = context === 'menu';
          return (
            <div
              style={{
                display: !isMenu && !isMulti && focused ? 'none' : 'block',
                cursor: 'pointer',
              }}
            >
              <DropdownCard
                {...getCardData(option, isMenu)}
                size={!isMenu && isMulti ? 'sm-md' : 'md'}
                weight={!isMenu ? 'regular' : 'semiBold'}
              />
            </div>
          );
        }}
        onChange={(value, actionMeta) => {
          if (actionMeta?.action === 'clear' && onClear) {
            onClear();
          } else {
            onChange(value);
          }
          setFocused(false);
        }}
      />
    </div>
  );
}
